import { useState, useEffect, useCallback } from 'react';
import { Store } from '../../../types';
import { getAllStores } from '../../../services/api';

// Shared cache for stores data
let storesCache: {
  data: Store[] | null;
  isLoading: boolean;
  error: any;
  subscribers: Set<() => void>;
} = {
  data: null,
  isLoading: false,
  error: null,
  subscribers: new Set(),
};

// Function to fetch stores if not already cached
const fetchStoresIfNeeded = async () => {
  if (storesCache.data || storesCache.isLoading) {
    return;
  }

  storesCache.isLoading = true;
  storesCache.error = null;
  notifySubscribers();

  try {
    const stores = await getAllStores();
    storesCache.data = stores;
    storesCache.isLoading = false;
    storesCache.error = null;
  } catch (error) {
    storesCache.data = null;
    storesCache.isLoading = false;
    storesCache.error = error;
  }

  notifySubscribers();
};

// Function to notify all subscribers of state changes
const notifySubscribers = () => {
  storesCache.subscribers.forEach(callback => callback());
};

// Function to reset the cache (useful when navigating away)
export const resetStoresCache = () => {
  storesCache.data = null;
  storesCache.isLoading = false;
  storesCache.error = null;
  notifySubscribers();
};

// Hook to access shared stores data
export const useSharedStores = () => {
  const [, forceUpdate] = useState({});

  const triggerUpdate = useCallback(() => {
    forceUpdate({});
  }, []);

  useEffect(() => {
    // Add this component as a subscriber
    storesCache.subscribers.add(triggerUpdate);

    // Fetch stores if needed
    fetchStoresIfNeeded();

    // Cleanup: remove subscriber when component unmounts
    return () => {
      storesCache.subscribers.delete(triggerUpdate);
    };
  }, [triggerUpdate]);

  return {
    allStores: storesCache.data,
    isLoading: storesCache.isLoading,
    error: storesCache.error,
  };
};